import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone, Scale } from "lucide-react";
import { useSettings, telHref } from "@/context/SettingsContext";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/about", label: "Profile" },
  { to: "/practice-areas", label: "Practice Areas" },
  { to: "/blog", label: "Blog" },
  { to: "/contact", label: "Contact" },
];

export function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const settings = useSettings();
  const loc = useLocation();
  const c = settings?.contact || {};

  useEffect(() => { setOpen(false); }, [loc.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header className="sticky top-0 z-50" data-testid="site-header">
      {/* Top bar */}
      <div className="hidden md:block bg-navy-dark text-ivory/70 text-xs">
        <div className="max-w-7xl mx-auto px-6 py-2 flex justify-between items-center">
          <span className="font-mono tracking-wide">Thrissur Office · KMA Towers, Kanjani Road, West Fort</span>
          <div className="flex items-center gap-4">
            <a href={telHref(c.phone1)} className="hover:text-brass transition-colors" data-testid="topbar-phone1">{c.phone1 || "+91 75598 97677"}</a>
            <span className="text-ivory/30">|</span>
            <a href={telHref(c.phone2)} className="hover:text-brass transition-colors" data-testid="topbar-phone2">{c.phone2 || "+91 96457 99817"}</a>
          </div>
        </div>
      </div>
      {/* Main nav */}
      <div className={`backdrop-blur-md border-b transition-all ${scrolled ? "bg-navy/95 border-brass/30" : "bg-navy border-brass/10"}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between h-16 lg:h-20">
          <Link to="/" className="flex items-center gap-3 group" data-testid="header-logo">
            <span className="w-9 h-9 rounded-sm border border-brass/50 flex items-center justify-center text-brass group-hover:bg-brass/10 transition-colors">
              <Scale size={18} />
            </span>
            <span className="leading-none">
              <span className="block text-ivory font-serif text-sm lg:text-base tracking-wide whitespace-nowrap">Adv. Guruvayoorappan K V</span>
              <span className="block text-brass text-[10px] lg:text-xs font-mono tracking-[0.2em] uppercase">Advocate · Thrissur</span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-8">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.to === "/"}
                className={({ isActive }) => `text-sm font-medium tracking-wide transition-colors relative py-1 ${isActive ? "text-brass" : "text-ivory/80 hover:text-brass"}`}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}>
                {n.label}
              </NavLink>
            ))}
            <Link to="/contact" data-testid="header-consultation-cta"
              className="ml-2 bg-brass hover:bg-brass-hover text-navy font-semibold text-sm px-5 py-2.5 rounded-sm transition-colors tracking-wide">
              Request a Consultation
            </Link>
          </nav>

          <button className="lg:hidden text-ivory p-2" onClick={() => setOpen(!open)} aria-label="Menu" data-testid="mobile-menu-toggle">
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {open && (
          <div className="lg:hidden bg-navy border-t border-brass/20 animate-fade-in" data-testid="mobile-nav">
            <nav className="px-4 py-4 flex flex-col gap-1">
              {NAV.map((n) => (
                <NavLink key={n.to} to={n.to} end={n.to === "/"}
                  className={({ isActive }) => `px-3 py-3 rounded-sm text-base font-medium ${isActive ? "text-brass bg-brass/10" : "text-ivory/85"}`}
                  data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}>
                  {n.label}
                </NavLink>
              ))}
              <a href={telHref(c.phone1)} className="mt-2 flex items-center justify-center gap-2 border border-brass/40 text-brass px-4 py-3 rounded-sm">
                <Phone size={16} /> Call {c.phone1 || "+91 75598 97677"}
              </a>
              <Link to="/contact" className="bg-brass text-navy font-semibold px-4 py-3 rounded-sm text-center">
                Request a Consultation
              </Link>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}
